import React, {useEffect, useState} from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import {schedulerApi} from '../../../services'
import {useNavigate, useParams} from 'react-router-dom'
import WbButton from '../../common/WbButton'
import WbAlert from '../../common/WbAlert'
import paths from '../../../routes/paths'
import EmojiQrWarning from './EmojiQrWarning'

export default function PublishButton({survey, onSave}) {
  const navigate = useNavigate()
  const token = useParams().token
  const [canPulish, setCanPublish] = useState(false)
  const [hide, setHide] = useState(true)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    // Guarded because the button is rendered from whatever the review page loaded, and that fetch
    // has no error branch: a failed load leaves survey null, which used to throw out of this effect
    // on the first property read and take the page down with it.
    if (!survey) {
      setCanPublish(false)
      return
    }

    const {
      participants,
      startDateTime,
      endDateTime,
      staffNotifications,
      parentNotifications,
      pupilNotifications,
      questionCount,
    } = survey
    if (
      questionCount === 0 ||
      participants?.length === 0 ||
      endDateTime?.toLocaleLowerCase() === 'not scheduled' ||
      startDateTime?.toLocaleLowerCase() === 'not scheduled' ||
      !endDateTime ||
      !startDateTime
    ) {
      setCanPublish(false)
      return
    }

    // The backend requires BOTH a subject and a message for each audience's
    // notification (else it returns NO_NOTIFICATION). Mirror that here so the
    // Publish button isn't enabled for a survey the backend would reject.
    const notifIncomplete = n => {
      try {
        const p = JSON.parse(n)
        return !p || !p.Subject || !p.Message
      } catch {
        return true
      }
    }
    if (
      (participants?.some(x => x.audience === 1) && notifIncomplete(staffNotifications)) ||
      (participants?.some(x => x.audience === 2) && notifIncomplete(parentNotifications)) ||
      (participants?.some(x => x.audience === 4) && notifIncomplete(pupilNotifications))
    ) {
      setCanPublish(false)
      return
    }

    setCanPublish(true)
  }, [survey])

  const publish = () => {
    setPaused(true)
    schedulerApi
      .publish(token)
      // res is a PublishDoneStatus code: 1=FAILED, 2=SUCCESS, 3=ALREADY_PUBLISHED,
      // 4=RUNNING_OR_CLOSED, 5/6=NO_(GROUP)CYCLES, 7=NO_QUESTIONS, 8=NO_TAGS,
      // 9=SHOW_QR_EMOJI_WARNING, 10=NO_NOTIFICATION.
      .then(res => {
        switch (res) {
          case 2: // SUCCESS
          case 3: // ALREADY_PUBLISHED
            // Redirect to the survey list (home) FIRST so a toast failure can never
            // block the redirect; the success toast renders into its own #modal root
            // and still shows on the destination page.
            navigate(paths.dashboard)
            WbAlert({message: 'Survey published successfully.', type: 'success'})
            break
          case 1: // FAILED
            WbAlert({
              message: 'Failed to publish the survey. Please try again.',
              type: 'error',
            })
            break
          case 4: // RUNNING_OR_CLOSED
            WbAlert({
              message: 'Survey is live and cannot be published again.',
              type: 'error',
            })
            break
          // The cases below are pre-publish validations (survey NOT published):
          // surface them as warnings so the user knows what to complete, not as
          // hard errors.
          case 5: // NO_GROUPCYCLES
          case 6: // NO_CYCLES
            WbAlert({message: 'Survey has no cycles.', type: 'warning'})
            break
          case 7: // NO_QUESTIONS
            WbAlert({message: 'Survey has no questions.', type: 'warning'})
            break
          case 8: // NO_TAGS
            WbAlert({message: 'Survey has no tags.', type: 'warning'})
            break
          case 9: // SHOW_QR_EMOJI_WARNING
            setHide(false)
            break
          case 10: // NO_NOTIFICATION
            WbAlert({
              message:
                'Please add a notification subject and message for each audience before publishing.',
              type: 'warning',
            })
            break
          default:
            WbAlert({
              message: 'Could not publish the survey. Please try again.',
              type: 'error',
            })
        }
      })
      .catch(error => {
        // A timeout means we never heard back, not that publishing failed - the survey is very
        // likely live. Saying "failed" here is what sent people back to press Publish again on a
        // survey that had already published.
        if (error?.timedOut) {
          WbAlert({
            message:
              'Publishing is taking longer than expected. Check the survey list before trying again.',
            type: 'warning',
          })
          return
        }
        WbAlert({message: 'Failed to publish survey', type: 'error'})
      })
      .finally(() => setPaused(false))
  }

  return (
    <>
      <EmojiQrWarning setHide={setHide} hide={hide} />
      <WbButton
        disabled={paused || canPulish === false}
        // Greying out on its own is indistinguishable from a broken button, which is exactly how
        // this read while the request was outstanding. Say what is happening.
        CustomButtonText={paused ? 'Publishing…' : 'Publish'}
        startIcon={paused ? <CircularProgress size={16} color="inherit" /> : null}
        onClick={async () => {
          if (onSave) {
            var done = await onSave()
            if (done) {
              publish()
            }
          } else {
            publish()
          }
        }}
      />
    </>
  )
}
